import { useCallback, useEffect, useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Tag, AlertTriangle, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import OfferCard from "./OfferCard";
import OfferEditor from "./OfferEditor";
import { OFFER_TEMPLATES } from "./OfferTemplateForm";

/**
 * The offers on one campaign (spec 7.1, 9).
 *
 * Self-contained in the ContentManager mould: owns its state and its API calls, mounted
 * from the campaign form in one line.
 *
 * Tier caps are Bronze 0, Silver 3, Gold 5. The cap is shown and Add is disabled at the
 * limit, so a merchant is not invited to fill a form that will be refused. When it is
 * refused anyway - a concurrent add, a stale page - the backend's message is surfaced
 * verbatim, because spec 10.5 specifies that wording.
 */

// Mirrors CAMPAIGN_TIER_CONFIG max_offers. Only used to shape the UI; the backend is
// authoritative and rejects anything over the cap regardless.
const TIER_OFFER_CAPS = { BRONZE: 0, SILVER: 3, GOLD: 5 };

// Mirrors BOOST_OFFERS_PER_ADDON in campaign_rules.py. Ten Feature Offers per Boost.
const BOOST_OFFERS_PER_BOOST = 10;

/**
 * Round 4, G-8. This component is mounted twice on the campaign form: once for the
 * tier's own offers and once inside the Discovery Boost panel, so the Feature Offers a
 * merchant has just bought are created where they bought them.
 *
 * `source` selects which. It is also a real fix, not only a placement: nothing sent a
 * source before, so every offer this component created was a TIER offer and a Boost
 * offer was not creatable from the UI at all. The backend path for one has existed
 * since the Boost work, with no way in.
 *
 * `boostQuantity` is how many Boosts have been PAID for, so the cap follows the
 * purchase. An unpaid Boost passes 0 and the panel explains rather than offering a form
 * the backend will refuse with a 402.
 */
export default function OfferManager({
  campaignId,
  tier = "BRONZE",
  pendingTier = null,
  onCountChange,
  source = "TIER",
  boostQuantity = 0,
  // The campaign's own Content, passed straight through to the editor and the cards so
  // each offer can carry its own creative (round 7, SP-26 / D6).
  campaignContent = [],
}) {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  // An unset tier is NOT Bronze. The campaign form initialises tier to "" and only
  // sets it once the merchant chooses, and a default prop does not apply to an
  // explicit "" - so without this the form would announce Bronze's rules before any
  // tier had been picked.
  const knownTier = Object.prototype.hasOwnProperty.call(TIER_OFFER_CAPS, tier);
  const isBoost = source === "DISCOVERY_BOOST";
  // Boost offers sit outside the tier cap (8.1) and have their own, ten per Boost
  // bought (G-7). Each mode counts only its own offers - the grid used to render both
  // kinds under a caption that counted one, so a merchant with Boost offers saw a list
  // whose number did not match its contents.
  const tierOffers = offers.filter((o) => o.source !== "DISCOVERY_BOOST");
  const boostOffers = offers.filter((o) => o.source === "DISCOVERY_BOOST");
  const shown = isBoost ? boostOffers : tierOffers;
  const cap = isBoost
    ? (boostQuantity || 0) * BOOST_OFFERS_PER_BOOST
    : (TIER_OFFER_CAPS[tier] ?? 0);
  const atCap = shown.length >= cap;

  // `tier` is the SAVED tier; `pendingTier` is set when the form is showing a different
  // one that has not been saved yet. The backend enforces the cap against the saved
  // tier, so the two must not be conflated: showing Gold's "3 of 5" while the campaign
  // is still persisted as Silver invites a fourth offer that will be refused.
  //
  // Adding is blocked while a change is pending rather than merely capped, because the
  // merchant is looking at a Gold form - a disabled Add with no explanation reads as a
  // bug. Editing and removing stay available on purpose: clearing offers down to the
  // lower cap is exactly what a pending downgrade requires.
  // A pending tier change cannot affect the Boost cap: Boost sits outside the tier, so
  // switching Silver to Gold neither grants nor removes a Feature Offer.
  const pendingCap = (!isBoost && pendingTier) ? (TIER_OFFER_CAPS[pendingTier] ?? 0) : null;
  const mustRemove = pendingCap !== null ? Math.max(0, tierOffers.length - pendingCap) : 0;
  const label = (t) => (t ? t.charAt(0) + t.slice(1).toLowerCase() : "");
  const addBlocked = atCap || (!isBoost && !!pendingTier);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/offers`, auth);
      setOffers(res.data || []);
      onCountChange?.((res.data || []).length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load offers");
    } finally {
      setLoading(false);
    }
  }, [campaignId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Admin-managed and rarely changing, so fetched once alongside the offers. Failing
  // quietly is deliberate: a missing category list must not block offer editing, since
  // the field is optional.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/offer-categories`, auth)
      .then((res) => { if (!cancelled) setCategories(res.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (body) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/offers/${editing.id}`, body, auth);
        toast.success("Offer updated");
      } else {
        // The source, which nothing sent before (G-8). Without it every offer created
        // here was a TIER offer, so a Feature Offer was not creatable from the UI at
        // all and the Boost a merchant paid for had nothing to hold.
        await axios.post(
          `${API}/campaigns/${campaignId}/offers`,
          { ...body, source },
          auth
        );
        toast.success(isBoost ? "Feature Offer added" : "Offer added");
      }
      setEditorOpen(false);
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
    // Errors deliberately propagate: OfferEditor catches them and keeps the dialog
    // open with the backend's message, rather than losing what the merchant typed.
  };

  // Fetched when the popover opens rather than on click, so the trigger keeps Radix's
  // own toggle behaviour and the panel can show its own loading state. The body is
  // empty on purpose: every field of DealSuggestionRequest is optional and the backend
  // fills them from the account.
  const openSuggestions = async (open) => {
    setSuggestOpen(open);
    if (!open || suggestions.length || suggesting) return;
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/campaigns/suggest-deal`, {}, auth);
      setSuggestions(res.data?.suggestions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate suggestions");
      setSuggestOpen(false);
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = (sug) => {
    // The backend infers a template from its own copy; the merchant reviews every
    // field in the editor before anything is saved.
    setSuggestOpen(false);
    setEditing(null);
    setPrefill({ template: sug.suggested_template, payload: sug.suggested_payload });
    setEditorOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/offers/${pendingDelete.id}`, auth);
      toast.success("Offer removed");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove the offer");
    }
  };

  if (!campaignId) {
    return (
      <p className="text-sm text-muted-foreground">
        Save the campaign first, then add its offers.
      </p>
    );
  }

  // Boost mode skips every tier-shaped explainer below. Boost is bought per campaign
  // and works on Bronze - it is the ONLY way a Bronze campaign reaches Discovery - so
  // "this tier carries no offers" would be the wrong answer to every question here.
  if (isBoost) {
    if (cap === 0) {
      return (
        <p className="text-xs text-muted-foreground" data-testid="boost-offers-locked">
          Your Feature Offers unlock once the Discovery Boost payment clears. You will be
          able to create {BOOST_OFFERS_PER_BOOST} here for each Boost you buy.
        </p>
      );
    }
  } else if (!knownTier) {
    return (
      <p className="text-sm text-muted-foreground">
        Choose a campaign tier to see the offers it supports.
      </p>
    );
  }

  // Saved as Bronze, but the form is showing a tier that does carry offers. The Bronze
  // explainer below would be the wrong answer to the question the merchant is asking -
  // they have already switched the picker, and want to know why no offers appeared.
  if (!isBoost && cap === 0 && pendingTier && (TIER_OFFER_CAPS[pendingTier] ?? 0) > 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-4 space-y-1.5">
          <p className="text-sm font-medium flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            Save the change to {label(pendingTier)} to add offers
          </p>
          <p className="text-xs text-muted-foreground">
            This campaign is still saved as {label(tier)}, which carries no offers. Once
            the tier change is saved you can add up to {TIER_OFFER_CAPS[pendingTier]}.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Bronze holds no offers at all (spec 9.4, 13.1). Its single asset is the campaign
  // creative, which is not an offer - so show no offer UI, and explain why rather than
  // presenting a disabled control with no reason.
  if (!isBoost && cap === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-4 space-y-1.5">
          <p className="text-sm font-medium flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            Bronze runs a static awareness creative
          </p>
          <p className="text-xs text-muted-foreground">
            {/* Joel's wording, near-verbatim (SP-10). His version is better on three
                counts: "do not include clickable offers" rather than "do not carry
                offers", which was untrue of a Bronze campaign with Discovery Boost;
                "favourite to follow your venue", which says what favouriting is FOR;
                and Silver OR Gold, since the old line implied Silver was the only way
                up. */}
            Bronze campaigns do not include clickable offers. They show a single static
            creative that customers can favourite to follow your venue. Upgrade to Silver
            or Gold to include redeemable offers, or add Discovery Boost to have featured
            offers appear in Discovery.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Label>{isBoost ? "Feature Offers" : "Offers"}</Label>
          <Badge variant={atCap ? "secondary" : "outline"} className="text-[11px]">
            {shown.length} of {cap}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={suggestOpen} onOpenChange={openSuggestions}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost" disabled={addBlocked}>
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Suggest
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-2" align="end">
              {suggesting ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground p-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Thinking of offers for your business…
                </div>
              ) : suggestions.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">No suggestions returned.</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground px-2 pb-1">
                    Pick one to open the editor pre-filled. Nothing is saved until you confirm.
                  </p>
                  {suggestions.map((sug, i) => (
                    <button
                      key={i} type="button"
                      onClick={() => applySuggestion(sug)}
                      className="w-full text-left rounded-md p-2 hover:bg-accent transition-colors"
                    >
                      <p className="text-sm font-medium">{sug.offer_text}</p>
                      <p className="text-[11px] text-muted-foreground line-clamp-2">
                        {sug.deal_description}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
          {/* "Add offer" is gone (round 7, SP-17). It opened a dialog whose first
              control was a dropdown of the nine templates, so it was a button in front
              of a list in front of a form. The templates are now the list itself, below,
              and Joel struck the button out in his own screenshot. */}
        </div>
      </div>

      {(!isBoost && pendingTier) ? (
        <p className="text-[11px] text-amber-600 font-medium">
          {mustRemove > 0 ? (
            <>
              {label(pendingTier)} allows {pendingCap} offer{pendingCap === 1 ? "" : "s"}.
              Remove {mustRemove} to save the change to {label(pendingTier)}.
            </>
          ) : (
            <>
              Save the change to {label(pendingTier)} first - offers are limited by the
              tier this campaign is saved on, still {label(tier)}.
            </>
          )}
        </p>
      ) : atCap && (
        <p className="text-[11px] text-muted-foreground">
          {isBoost ? (
            <>
              You have created all {cap} Feature Offers your Discovery Boost
              {boostQuantity > 1 ? "es" : ""} buy. Remove one to add another
              {boostQuantity < 2 ? ", or buy a second Boost" : ""}.
            </>
          ) : (
            <>
              This campaign has reached its {cap}-offer limit for {label(tier)}.
              Remove one to add another{tier !== "GOLD" ? ", or upgrade the tier" : ""}.
            </>
          )}
        </p>
      )}

      {/* The templates, as containers (SP-17). Joel's sketch draws these across the
          strip where "No offers yet" sat, and strikes out the Add offer button above.

          Visible while there is ROOM rather than only when the list is empty: a Gold
          campaign with two of five should still see them, or the merchant is back to
          hunting for a button after their first offer.

          Clicking one is the same act as accepting an AI suggestion, so it reuses the
          same `prefill` the editor already takes - nothing new in the editor at all. */}
      {!addBlocked && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
          {/* All nine, for Boost offers too. A Feature Offer is an offer and there is
              no reason a merchant should have fewer kinds to choose from because they
              bought it as an add-on. */}
          {OFFER_TEMPLATES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => {
                setEditing(null);
                setPrefill({ template: t.value });
                setEditorOpen(true);
              }}
              className="rounded-lg border border-border bg-card p-2.5 text-left transition-colors hover:border-primary hover:bg-accent/40"
              data-testid={`offer-template-${t.value}`}
            >
              {/* Round 9, TP-6. Nine tiles of the same grey text are read by scanning
                  every label; an icon makes the one you want findable at a glance. */}
              <span className="flex items-center gap-1.5">
                {t.icon && <t.icon className="h-3.5 w-3.5 text-primary shrink-0" />}
                <span className="text-xs font-medium leading-tight">{t.label}</span>
              </span>
              <span className="block text-[10px] text-muted-foreground leading-snug mt-0.5">
                {t.hint}
              </span>
            </button>
          ))}
        </div>
      )}

      {loading && shown.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading offers…
        </div>
      ) : shown.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              {/* No longer says "add" anything: the templates above ARE the way in
                  (SP-17), so this only says how many and what happens to them.
                  Wording is Joel's own, verbatim (round 9, TP-4 and TP-5). */}
              {isBoost
                ? "Select an offer template above to create your Featured Offers. Each appears as its own card in Discovery."
                : `Select an offer template above to create your first offer. You can add up to ${cap} for this campaign. These will appear in Discovery and be shown to your targeted audience when they are on the move.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        // Three across, not two (SP-26): "once offers are made the containers are too
        // larger should not be two per line they can be much much smaller". Joel's
        // sketch shows roughly three compact cards where two large ones sat.
        //
        // A `//` comment, not a {/* */} one: this is the false branch of a ternary,
        // which takes a single expression, and a JSX comment there is a second child.
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {shown.map((o) => (
            <OfferCard
              key={o.id}
              offer={o}
              creative={campaignContent.find((c) => c.id === o.content_id) || null}
              campaignCreative={campaignContent[0] || null}
              onEdit={(offer) => { setEditing(offer); setEditorOpen(true); }}
              onDelete={setPendingDelete}
            />
          ))}
        </div>
      )}

      <OfferEditor
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) { setEditing(null); setPrefill(null); }
        }}
        campaignTier={tier}
        offer={editing}
        prefill={prefill}
        campaignContent={campaignContent}
        categories={categories}
        onSave={save}
        saving={saving}
      />

      <Dialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Remove this offer?
            </DialogTitle>
            <DialogDescription>
              &ldquo;{pendingDelete?.display_value}&rdquo; will be removed from this
              campaign. Offers from expired campaigns are kept in Discovery Offers, but
              removing one here deletes it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={confirmDelete}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Local Label so this component does not depend on the page importing one for it.
function Label({ children, className = "" }) {
  return <span className={`text-sm font-medium ${className}`}>{children}</span>;
}
